import type { ReactNode } from 'react'
import React, { useContext as useContextCore } from 'react'
import map from 'lodash/map'
import type { UseFavoriteReportsResult } from './useFavoriteReports'
import { useFavoriteReports } from './useFavoriteReports'
import type { UseListResult } from '@ztechapps/common.hook'
import { useUser, useTemplateList } from '@ztechapps/common.hook'

type ContextProps = { children: ReactNode; reportFilters: any[]; reportTemplateCategories: any[]; appName: string }

export type ContextType = {
  templateCategories: string[]
} & Omit<ContextProps, 'children'> &
  UseListResult &
  UseFavoriteReportsResult

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({ children, reportFilters, reportTemplateCategories, appName }: ContextProps) => {
  const { user } = useUser()

  const templateCategories = React.useMemo(
    () => map(reportTemplateCategories, (m) => m?.name),
    [reportTemplateCategories]
  )

  const filter = {
    category: { $in: templateCategories },
    $or: [
      { $and: [{ 'scope.key': 'user' }, { 'scope.value': user?._id }] },
      { $and: [{ 'scope.key': 'tenant' }, { 'scope.value': user?.tenantId }] },
      { $and: [{ 'scope.key': 'app' }, { 'scope.value': appName }] },
    ],
  }
  const sort = { name: 1 as const }

  const { list, ...listResult } = useTemplateList({ filter, sort })

  const favoritesResult = useFavoriteReports({ templateList: list })

  return (
    <CoreProvider
      value={{
        list,
        ...listResult,
        ...favoritesResult,
        reportTemplateCategories,
        reportFilters,
        appName,
        templateCategories,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
